import { useMemo } from 'react'

import type { AnyAction, Dispatch } from 'redux'

import { BridgeService } from '@services/bridge'
import { generateIdUnicoLinkRequest, postBiometriaDadoSave } from '@store/biometria/actions'
import type { BiometricActiveValue } from '@store/biometria/types'
import { ConveniosCodes, StepRefin } from '@utils/enums'
import { isIdCloudBiometric, isIdCloudWithDocuments, isSerasaBiometric } from '@utils/functions'
import routes from '@routes/routes'

import { sendAppDocumentTag, sendWebDocumentTag } from './tracking'

type ConvenioSelecionadoValue = string | number | undefined

export interface GetIsButtonDisabledParams {
  convenioSelecionado: ConvenioSelecionadoValue
  hasOtherInAppDocuments: boolean
  idCloudWithDocumentsActive: boolean
  isDone: boolean
  isDoneContraCheque: boolean
  showsContracheque: boolean
}

function getIsButtonDisabled({
  convenioSelecionado,
  hasOtherInAppDocuments,
  idCloudWithDocumentsActive,
  isDone,
  isDoneContraCheque,
  showsContracheque,
}: GetIsButtonDisabledParams): boolean {
  if (idCloudWithDocumentsActive) {
    if (!hasOtherInAppDocuments) {
      return false
    }
    if (showsContracheque) {
      return !isDoneContraCheque
    }
    return false
  }

  return (
    !isDone ||
    (ConveniosCodes.SIAPE_SERVIDOR_FEDERAL === convenioSelecionado && !isDoneContraCheque)
  )
}

function getStatusDocumentosHeadingTitle(convenioSelecionado: ConvenioSelecionadoValue): string {
  return ConveniosCodes.SIAPE_SERVIDOR_FEDERAL === convenioSelecionado
    ? 'Envie os documentos abaixo'
    : 'Envie o seu documento de identificação'
}

function getIdentificacaoDocumentCaption(convenioSelecionado: ConvenioSelecionadoValue): string {
  if (
    convenioSelecionado === ConveniosCodes.INSS ||
    convenioSelecionado === ConveniosCodes.SIAPE_SERVIDOR_FEDERAL
  ) {
    return 'CNH, RG, CIN, DNI, RNE, RNM ou Carteira funcional'
  }
  return 'CNH, RG, CIN, DNI, RNE ou RNM'
}

export interface GetStatusDocumentosViewFlagsParams {
  convenioSelecionado: ConvenioSelecionadoValue
  isError: boolean
  biometricActive: BiometricActiveValue
  loadingGenerateIdUnicoLink: boolean
}

export interface StatusDocumentosViewFlags {
  showsContracheque: boolean
  hasOtherInAppDocuments: boolean
  idCloudWithDocumentsActive: boolean
  isContinuarIdUnicoLoading: boolean
  showStatusDocumentosErrorAlert: boolean
}

function getStatusDocumentosViewFlags({
  convenioSelecionado,
  isError,
  biometricActive,
  loadingGenerateIdUnicoLink,
}: GetStatusDocumentosViewFlagsParams): StatusDocumentosViewFlags {
  const showsContracheque = ConveniosCodes.SIAPE_SERVIDOR_FEDERAL === convenioSelecionado
  const hasOtherInAppDocuments = showsContracheque

  const idCloudWithDocumentsActive = isIdCloudWithDocuments(biometricActive)
  const isContinuarIdUnicoLoading =
    isIdCloudBiometric(biometricActive) && loadingGenerateIdUnicoLink
  const showStatusDocumentosErrorAlert = isError && !idCloudWithDocumentsActive

  return {
    showsContracheque,
    hasOtherInAppDocuments,
    idCloudWithDocumentsActive,
    isContinuarIdUnicoLoading,
    showStatusDocumentosErrorAlert,
  }
}

export interface ExecuteStatusDocumentosContinueParams {
  isApp: boolean
  biometricActive: BiometricActiveValue
  dispatch: Dispatch<AnyAction>
  trackingRefin: (step: StepRefin) => AnyAction
  navigate: (path: string) => void
}

function executeStatusDocumentosContinue({
  isApp,
  biometricActive,
  dispatch,
  trackingRefin,
  navigate,
}: ExecuteStatusDocumentosContinueParams): void {
  BridgeService.tic({
    name: 'btn_solicitar_biometria',
    action: 'solicitar_biometria',
  })

  if (isApp) {
    sendAppDocumentTag({
      ref_type: 'botao',
      content_action: 'Toque',
    })
  } else {
    sendWebDocumentTag({ element_action: 'click button' })
  }

  if (isSerasaBiometric(biometricActive)) {
    dispatch(trackingRefin(StepRefin.INICIO_CAPTURA_BIOMETRIA_SERASA))
  }

  dispatch(
    postBiometriaDadoSave({
      callback: () => {
        dispatch(trackingRefin(StepRefin.ORIENTACOES_VIDEO_SELFIE))

        if (isIdCloudBiometric(biometricActive)) {
          dispatch(
            generateIdUnicoLinkRequest({
              callback: (biometricLink: string) => {
                window.location.href = biometricLink
              },
            }),
          )
          return
        }

        navigate(routes.BIOMETRIA_LIVENESS)
      },
    }),
  )
}

export interface UseStatusDocumentosHelpersResult {
  executeStatusDocumentosContinue: (params: ExecuteStatusDocumentosContinueParams) => void
  getIdentificacaoDocumentCaption: (convenioSelecionado: ConvenioSelecionadoValue) => string
  getIsButtonDisabled: (params: GetIsButtonDisabledParams) => boolean
  getStatusDocumentosHeadingTitle: (convenioSelecionado: ConvenioSelecionadoValue) => string
  getStatusDocumentosViewFlags: (
    params: GetStatusDocumentosViewFlagsParams,
  ) => StatusDocumentosViewFlags
}

export function useStatusDocumentosHelpers(): UseStatusDocumentosHelpersResult {
  return useMemo(
    () => ({
      executeStatusDocumentosContinue,
      getIdentificacaoDocumentCaption,
      getIsButtonDisabled,
      getStatusDocumentosHeadingTitle,
      getStatusDocumentosViewFlags,
    }),
    [],
  )
}
